import { gql, useQuery } from '@apollo/client';
import Table, { TableCard } from '@orn/shared-web/components/Table';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { useClipboard } from '@orn/shared-web/hooks/useClipboard';
import { CheckIcon, CopyIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { filterNullsAndUndefined } from '../../../utils/filterUtils';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';

import type {
  AdminGetTransactionPairsQuery,
  AdminGetTransactionPairsQueryVariables,
} from './__generated__/TransactionPairs.gql';

const ADMIN_GET_TRANSACTION_PAIRS = gql`
  query AdminGetTransactionPairs($userId: String!) {
    adminGetTransactionPairsForUser(userId: $userId) {
      id
      pairType
      debitTransaction {
        id
        amount
        date
        description
        rawJson
        merchant {
          id
          name
        }
        financialAccount {
          id
          name
        }
      }
      creditTransaction {
        id
        amount
        date
        description
        rawJson
        merchant {
          id
          name
        }
        financialAccount {
          id
          name
        }
      }
    }
  }
`;

const CopyButton = ({
  text,
  ariaLabel,
  iconSize = 'h-3 w-3',
  className = '',
}: {
  text: string;
  ariaLabel: string;
  iconSize?: string;
  className?: string;
}) => {
  const { copy, copied } = useClipboard();

  return (
    <button
      type="button"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        void copy(text);
      }}
      className={`border-0 bg-transparent p-0 shadow-none hover:bg-transparent ${className}`}
      aria-label={ariaLabel}
      style={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}
    >
      {copied ? <CheckIcon className={iconSize} /> : <CopyIcon className={iconSize} />}
    </button>
  );
};

const TextWithCopyButton = ({ text }: { text: string }) => {
  return (
    <CopyButton
      text={text}
      ariaLabel="Copy"
      className="flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-600"
    />
  );
};

interface ITransactionPairsProps {
  userId: string;
}

const TransactionPairs = ({ userId }: ITransactionPairsProps) => {
  const { data, loading, error } = useQuery<AdminGetTransactionPairsQuery, AdminGetTransactionPairsQueryVariables>(
    ADMIN_GET_TRANSACTION_PAIRS,
    {
      variables: { userId },
      skip: !userId,
    }
  );

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  const pairsData = data?.adminGetTransactionPairsForUser || [];

  if (pairsData.length === 0) {
    return null;
  }

  return (
    <TableCard.Root className="min-w-180 shrink-0">
      <TableCard.Header title="Transaction pairs" description={`${pairsData.length} pairs`} />
      <Table
        aria-label="Transaction pairs table"
        className="**:select-text w-full [&_td]:select-text"
        selectionMode="none"
      >
        <Table.Header>
          <Table.Head label="Pair type" isRowHeader />
          <Table.Head label="Debit transaction" />
          <Table.Head label="Credit transaction" />
          <Table.Head label="Debit amount" className="flex justify-end" />
          <Table.Head label="Credit amount" className="flex justify-end" />
        </Table.Header>
        <Table.Body items={pairsData}>
          {(pair: AdminGetTransactionPairsQuery['adminGetTransactionPairsForUser'][number]) => {
            const debitAmount =
              typeof pair.debitTransaction.amount === 'string'
                ? parseFloat(pair.debitTransaction.amount)
                : pair.debitTransaction.amount;
            const creditAmount =
              typeof pair.creditTransaction.amount === 'string'
                ? parseFloat(pair.creditTransaction.amount)
                : pair.creditTransaction.amount;

            return (
              <Table.Row
                key={pair.id}
                data-pair-id={pair.id}
                className="**:select-text h-16"
                highlightSelectedRow={false}
              >
                <Table.Cell className="select-text">
                  <span className="text-base font-medium">{pair.pairType}</span>
                </Table.Cell>
                <Table.Cell className="select-text" style={{ userSelect: 'text' }}>
                  <div className="flex flex-col gap-1" style={{ userSelect: 'text' }}>
                    <div className="text-base font-medium">{pair.debitTransaction.description || 'N/A'}</div>
                    <div className="flex select-text flex-col gap-0.5 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Date: </span>
                        {pair.debitTransaction.rawJson ? (
                          <Tooltip
                            title="Transaction JSON"
                            wide
                            placement="right"
                            description={
                              <pre className="wrap-break-word whitespace-pre-wrap text-xs">
                                {JSON.stringify(filterNullsAndUndefined(pair.debitTransaction.rawJson), null, 2)}
                              </pre>
                            }
                          >
                            <TooltipTrigger>
                              <span className="cursor-help underline decoration-dotted">
                                {formatCalendarDate(pair.debitTransaction.date, 'medium')}
                              </span>
                            </TooltipTrigger>
                          </Tooltip>
                        ) : (
                          formatCalendarDate(pair.debitTransaction.date)
                        )}
                      </div>
                      <div>
                        <span className="font-medium">Account: </span>
                        {pair.debitTransaction.financialAccount?.name ?? 'N/A'}
                      </div>
                      {pair.debitTransaction.merchant?.name && (
                        <div>
                          <span className="font-medium">Merchant: </span>
                          {pair.debitTransaction.merchant.name}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="font-medium">ID: </span>
                        <span className="select-text">{pair.debitTransaction.id}</span>
                        <TextWithCopyButton text={pair.debitTransaction.id} />
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="select-text" style={{ userSelect: 'text' }}>
                  <div className="flex flex-col gap-1" style={{ userSelect: 'text' }}>
                    <div className="text-base font-medium">{pair.creditTransaction.description || 'N/A'}</div>
                    <div className="flex select-text flex-col gap-0.5 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Date: </span>
                        {pair.creditTransaction.rawJson ? (
                          <Tooltip
                            title="Transaction JSON"
                            wide
                            placement="right"
                            description={
                              <pre className="wrap-break-word whitespace-pre-wrap text-xs">
                                {JSON.stringify(filterNullsAndUndefined(pair.creditTransaction.rawJson), null, 2)}
                              </pre>
                            }
                          >
                            <TooltipTrigger>
                              <span className="cursor-help underline decoration-dotted">
                                {formatCalendarDate(pair.creditTransaction.date, 'medium')}
                              </span>
                            </TooltipTrigger>
                          </Tooltip>
                        ) : (
                          formatCalendarDate(pair.creditTransaction.date)
                        )}
                      </div>
                      <div>
                        <span className="font-medium">Account: </span>
                        {pair.creditTransaction.financialAccount?.name ?? 'N/A'}
                      </div>
                      {pair.creditTransaction.merchant?.name && (
                        <div>
                          <span className="font-medium">Merchant: </span>
                          {pair.creditTransaction.merchant.name}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="font-medium">ID: </span>
                        <span className="select-text">{pair.creditTransaction.id}</span>
                        <TextWithCopyButton text={pair.creditTransaction.id} />
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="select-text">
                  <div className="flex justify-end">
                    <span className="text-base font-medium">{toCurrency(debitAmount)}</span>
                  </div>
                </Table.Cell>
                <Table.Cell className="select-text">
                  <div className="flex justify-end">
                    <span className="text-base font-medium">{toCurrency(creditAmount)}</span>
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          }}
        </Table.Body>
      </Table>
    </TableCard.Root>
  );
};

export default TransactionPairs;
